type ScrollTarget = { scrollTop: number; scrollHeight: number; clientHeight: number };
type Options = {
  getTarget: () => ScrollTarget | null;
  requestFrame: (callback: (timestamp: number) => void) => number;
  cancelFrame: (id: number) => void;
  now: () => number;
};

/** One clock-driven loop, independent of dragover frequency and display refresh rate. */
export function createDragScroller({ getTarget, requestFrame, cancelFrame, now }: Options) {
  const pixelsPerSecond = 90;
  const maxFrameMilliseconds = 50;
  let direction: -1 | 0 | 1 = 0;
  let frame: number | null = null;
  let lastTime = 0;
  let remainder = 0;
  let generation = 0;

  const stop = () => {
    direction = 0;
    ++generation;
    if (frame !== null) cancelFrame(frame);
    frame = null;
    remainder = 0;
  };
  const tick = (timestamp: number, scheduledGeneration: number) => {
    if (scheduledGeneration !== generation || direction === 0) return;
    frame = null;
    const target = getTarget();
    if (!target) { stop(); return; }
    const elapsed = Math.max(0, Math.min(maxFrameMilliseconds, timestamp - lastTime));
    lastTime = timestamp;
    remainder += direction * pixelsPerSecond * elapsed / 1000;
    const pixels = Math.trunc(remainder);
    remainder -= pixels;
    target.scrollTop = Math.max(0, Math.min(Math.max(0, target.scrollHeight - target.clientHeight), target.scrollTop + pixels));
    frame = requestFrame((time) => tick(time, scheduledGeneration));
  };
  const start = (nextDirection: -1 | 1) => {
    if (direction === nextDirection) return;
    stop();
    direction = nextDirection;
    lastTime = now();
    const scheduledGeneration = generation;
    frame = requestFrame((timestamp) => tick(timestamp, scheduledGeneration));
  };
  return { start, stop };
}
